import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSend, FiCopy, FiInfo, FiHome, FiUser, FiClipboard, FiLayers } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  aprovacaoProjetosPortalService, TIPO_OBRA_LABEL, TIPO_OBRA_DESCRICAO, ZONA_USO_LABEL,
  type TipoObra, type ZonaUso,
} from '../../services/aprovacaoProjetos.service';

export function AprovacaoProjetosSubmeterPage() {
  const { cidadao, cadastroCompleto } = useAuth();
  const navigate = useNavigate();

  const [tipoObra, setTipoObra] = useState<TipoObra>('construcao_nova');
  const [titulo, setTitulo] = useState('');
  const [descricaoObra, setDescricaoObra] = useState('');

  // Imovel
  const [endereco, setEndereco] = useState('');
  const [numeroImovel, setNumeroImovel] = useState('');
  const [bairro, setBairro] = useState('');
  const [cep, setCep] = useState('');
  const [inscricao, setInscricao] = useState('');
  const [areaTerreno, setAreaTerreno] = useState<number | ''>('');
  const [areaConstrucao, setAreaConstrucao] = useState<number | ''>('');
  const [zonaUso, setZonaUso] = useState<ZonaUso>('residencial');

  // Responsavel tecnico
  const [rtNome, setRtNome] = useState('');
  const [rtCpf, setRtCpf] = useState('');
  const [rtRegistro, setRtRegistro] = useState('');
  const [rtArtRrt, setRtArtRrt] = useState('');

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ numero: string; hash_publico: string } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cidadao) return;
    if (titulo.trim().length < 5) { setErro('Informe um título descritivo do projeto.'); return; }
    if (descricaoObra.trim().length < 20) { setErro('Descreva a obra com mais detalhes (mín. 20 caracteres).'); return; }
    if (!endereco.trim() || !numeroImovel.trim() || !bairro.trim() || !cep.trim()) {
      setErro('Preencha o endereço completo do imóvel.');
      return;
    }
    if (!rtNome.trim() || !rtCpf.trim() || !rtRegistro.trim()) {
      setErro('Preencha os dados do responsável técnico (nome, CPF e registro CREA/CAU).');
      return;
    }

    setErro(null);
    setEnviando(true);
    try {
      const r = await aprovacaoProjetosPortalService.submeter({
        estabelecimento_id: getEstabelecimentoAtualId(),
        cidadao_id: cidadao.id,
        tipo_obra: tipoObra,
        descricao_obra: descricaoObra.trim(),
        titulo: titulo.trim(),
        imovel: {
          endereco: endereco.trim(),
          numero: numeroImovel.trim(),
          bairro: bairro.trim(),
          cep: cep.trim(),
          inscricao_imobiliaria: inscricao.trim() || undefined,
          area_terreno: areaTerreno === '' ? undefined : Number(areaTerreno),
          area_construcao: areaConstrucao === '' ? undefined : Number(areaConstrucao),
          zona_uso: zonaUso,
        },
        responsavel_tecnico: {
          nome: rtNome.trim(),
          cpf: rtCpf.trim(),
          registro: rtRegistro.trim(),
          art_rrt: rtArtRrt.trim() || undefined,
        },
      });
      setResultado({ numero: r.numero, hash_publico: r.hash_publico });
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao submeter projeto.');
    } finally {
      setEnviando(false);
    }
  }

  if (!cadastroCompleto) {
    return (
      <PortalPage
        titulo="Submeter projeto para aprovação"
        subtitulo="Alvarás de construção / reforma / ampliação — Lei 6.766/79 + leis municipais."
        icone={<FiClipboard size={26} />}
        voltarHref="/"
      >
        <SectionCard titulo="Complete seu cadastro" icone={<FiInfo size={18} />}>
          <Alert variante="aviso">
            Para submeter um projeto de aprovação você precisa completar seu cadastro
            (CPF, telefone e endereço).
          </Alert>
          <div className="flex justify-end mt-4">
            <Button onClick={() => navigate('/completar-cadastro')}>Completar cadastro</Button>
          </div>
        </SectionCard>
      </PortalPage>
    );
  }

  if (resultado) {
    return <PainelSucesso numero={resultado.numero} hash={resultado.hash_publico} onVer={() => navigate(`/solicitacao/${resultado.numero}`)} />;
  }

  return (
    <PortalPage
      titulo="Submeter projeto para aprovação"
      subtitulo="Alvarás de construção / reforma / ampliação — Lei 6.766/79 + leis municipais."
      icone={<FiClipboard size={26} />}
      voltarHref="/"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <Alert variante="info">
          <strong>Como funciona:</strong> após submeter, a equipe técnica analisará o projeto.
          Pode haver pedidos de correção (exigências) que você precisará atender. Quando aprovado,
          o alvará será emitido com validade conforme a lei municipal.
        </Alert>

        <SectionCard titulo="Tipo de obra" icone={<FiLayers size={18} />}>
          <div className="space-y-2">
            {(Object.keys(TIPO_OBRA_LABEL) as TipoObra[]).map((t) => (
              <label key={t} className={`block p-3 rounded-md border cursor-pointer transition ${
                tipoObra === t ? 'border-institutional bg-institutional/5' : 'border-slate-200 hover:border-slate-300'
              }`}>
                <div className="flex items-start gap-3">
                  <input type="radio" name="tipo-obra" checked={tipoObra === t} onChange={() => setTipoObra(t)} className="mt-1" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{TIPO_OBRA_LABEL[t]}</div>
                    <p className="text-xs text-slate-600 mt-0.5">{TIPO_OBRA_DESCRICAO[t]}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </SectionCard>

        <SectionCard titulo="Sobre o projeto" icone={<FiClipboard size={18} />}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="titulo" obrigatorio>Título do projeto</Label>
              <Input id="titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} required placeholder="Ex: Casa residencial unifamiliar 120m²" />
            </div>
            <div>
              <Label htmlFor="desc" obrigatorio>Descrição da obra</Label>
              <textarea
                id="desc"
                value={descricaoObra}
                onChange={(e) => setDescricaoObra(e.target.value)}
                rows={5}
                required
                placeholder="Detalhe a obra: número de pavimentos, finalidade, características construtivas..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm resize-y"
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard titulo="Dados do imóvel" icone={<FiHome size={18} />}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
            <div className="md:col-span-2">
              <Label htmlFor="end" obrigatorio>Endereço (rua/avenida)</Label>
              <Input id="end" value={endereco} onChange={(e) => setEndereco(e.target.value)} required placeholder="Ex: Av. Brasil" />
            </div>
            <div>
              <Label htmlFor="num" obrigatorio>Número</Label>
              <Input id="num" value={numeroImovel} onChange={(e) => setNumeroImovel(e.target.value)} required placeholder="1500" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="bairro" obrigatorio>Bairro</Label>
              <Input id="bairro" value={bairro} onChange={(e) => setBairro(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="cep" obrigatorio>CEP</Label>
              <Input id="cep" value={cep} onChange={(e) => setCep(e.target.value)} required placeholder="00000-000" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="inscricao">Inscrição imobiliária</Label>
              <Input id="inscricao" value={inscricao} onChange={(e) => setInscricao(e.target.value)} placeholder="Ex: 12.345.678-9" />
            </div>
            <div>
              <Label htmlFor="zona">Zona de uso</Label>
              <select
                id="zona"
                value={zonaUso}
                onChange={(e) => setZonaUso(e.target.value as ZonaUso)}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              >
                {(Object.keys(ZONA_USO_LABEL) as ZonaUso[]).map((z) => (
                  <option key={z} value={z}>{ZONA_USO_LABEL[z]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="area-ter">Área do terreno (m²)</Label>
              <Input id="area-ter" type="number" min="0" step="0.01" value={areaTerreno} onChange={(e) => setAreaTerreno(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div>
              <Label htmlFor="area-cons">Área de construção (m²)</Label>
              <Input id="area-cons" type="number" min="0" step="0.01" value={areaConstrucao} onChange={(e) => setAreaConstrucao(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
        </SectionCard>

        <SectionCard titulo="Responsável técnico" icone={<FiUser size={18} />}>
          <p className="text-xs text-slate-600 flex items-start gap-1 mb-4">
            <FiInfo size={12} className="flex-shrink-0 mt-0.5" />
            Engenheiro civil ou arquiteto registrado no CREA ou CAU. ART/RRT obrigatória conforme tipo de obra.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <div className="md:col-span-2">
              <Label htmlFor="rt-nome" obrigatorio>Nome completo</Label>
              <Input id="rt-nome" value={rtNome} onChange={(e) => setRtNome(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="rt-cpf" obrigatorio>CPF</Label>
              <Input id="rt-cpf" value={rtCpf} onChange={(e) => setRtCpf(e.target.value)} required placeholder="000.000.000-00" />
            </div>
            <div>
              <Label htmlFor="rt-reg" obrigatorio>Registro CREA/CAU</Label>
              <Input id="rt-reg" value={rtRegistro} onChange={(e) => setRtRegistro(e.target.value)} required placeholder="CREA-SP 12345 ou CAU A123456-7" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="rt-art">Número da ART/RRT</Label>
              <Input id="rt-art" value={rtArtRrt} onChange={(e) => setRtArtRrt(e.target.value)} placeholder="Anexar PDF na próxima etapa" />
            </div>
          </div>
        </SectionCard>

        <Alert variante="info">
          <strong>Próxima etapa:</strong> após submeter, você poderá anexar plantas, memorial descritivo,
          ART/RRT e demais documentos exigidos pela legislação municipal.
        </Alert>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/')}>Cancelar</Button>
          <Button type="submit" loading={enviando}>
            <FiSend size={14} /> Submeter projeto
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}

function PainelSucesso({ numero, hash, onVer }: { numero: string; hash: string; onVer: () => void }) {
  const link = `${window.location.origin}/protocolo/${numero}/${hash}`;
  const [copiado, setCopiado] = useState(false);
  function copiar() {
    navigator.clipboard.writeText(link);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <PainelResultado
      titulo="Projeto protocolado"
      descricao={<>Seu protocolo é <strong className="text-slate-900">{numero}</strong></>}
      acoes={<Button onClick={onVer}>Acompanhar projeto</Button>}
    >
      <p className="text-center text-xs text-slate-500">
        A equipe técnica analisará seu projeto. Você receberá notificações por e-mail
        em cada etapa (exigência ou decisão final).
      </p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Label>Link público de acompanhamento</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input value={link} readOnly className="text-xs flex-1" />
          <Button type="button" variant="outline" onClick={copiar}>
            <FiCopy size={14} /> {copiado ? 'Copiado!' : 'Copiar'}
          </Button>
        </div>
      </div>

      <Alert variante="info" className="mt-4">
        <strong>Próxima etapa:</strong> anexe as plantas (PDF), memorial descritivo e ART/RRT
        acessando "Acompanhar projeto".
      </Alert>
    </PainelResultado>
  );
}
